/* RC: GAD product page behaviour (sections/rc-main-product.liquid; spec 11 phase 3, page 50 approved 2026-09-26).
   Slice 1:
   - options -> variant: the hidden id inputs (cart form + Shop Pay installments form), price, Add to cart label and
     price, sold-out sizes for the chosen colour, ?variant= in the URL once the shopper has chosen everything.
   - colour -> gallery: slides, thumbnails and the add-on photo whose data-colour is another colour are hidden; the
     first photo of the colour (its lifestyle photo when there is one) comes up.
   - no size chosen yet: Add to cart reads "Choose a size"; pressing it nudges the sizes instead of adding. The same
     guard covers the express checkout buttons.
   - gallery: thumbnails, arrow keys, swipe on phones, dots; a video loads its player on press.
   - the buy box sticks under the header on desktop (--rcp-top from the header's position, as rc-collection.js).
   Slice 2:
   - one-tap add-on (the spare plate): the switch joins the add-on to Add to cart ("Add 2 items"), one cart request.
   - build your kit: this product + the kit's add-ons; the saving is 10 % of each add-on, rounded down to the cent
     the way checkout rounds it (Kit savings: buy 1 kit product, 10 % off up to 3 add-ons); one cart request.
   - bottom bar: links built from the page's sections (the lit one is the last whose top passed a third of the
     screen; a click keeps it lit while the page scrolls), and the buy side once Add to cart scrolls away.
   - decoration pop-up: interest chips feed the customer tags; after the form posts, the pop-up opens again.
   Slice 3:
   - photos in the content sections ([data-rcp-cv]: detail photos, ways, measurement and decoration figures) follow
     the colour like the gallery.
   - size guide: "Size guide" and "See the measurements" open the drawer; the chosen size is highlighted in both
     charts and its numbers sit on the measurement lines (the model's size until one is chosen).
   Operator 2026-09-26 (mockup 54, option C):
   - photos: hovering the big photo zooms into it where the pointer is (mouse only); clicking it opens the colour's
     photos large (arrows, swipe, Escape; click the large photo to see it full size and scroll around).
   - the bar stays on screen to the bottom of the page (the page gets that much room at the end) and shows
     Back to top once the last section is passed.
   Adding the product alone is Dawn's product-form.js (AJAX add + cart drawer); multi-item adds use /cart/add.js
   and hand the sections to the cart drawer.
   RC 2026-10-09 (EasyFlow stopgap): department products with "Your details" (snippets/rc-member-fields) are checked
   here and always added through /cart/add.js, the details as the line's properties (LAST NAME, EMPLOYEE#, Level Of
   Care) and, when a last name is typed, the name embroidery line (one per garment) beside it. */
if (!customElements.get('rc-product')) {
  customElements.define(
    'rc-product',
    class RcProduct extends HTMLElement {
      connectedCallback() {
        this.variants = this.readJson('[data-rcp-variants]', []);
        this.sel = this.readJson('[data-rcp-selected]', []);
        this.colourIndex = parseInt(this.dataset.colourIndex, 10);
        this.sizeIndex = parseInt(this.dataset.sizeIndex, 10);
        this.chart = this.readJson('[data-rcp-chart]', null);
        this.touched = false;
        this.addonOn = false;
        this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
        });

        this.slides = Array.from(this.querySelectorAll('[data-rcp-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-rcp-go]'));
        this.viewer = this.querySelector('[data-rcp-viewer]');
        this.dots = this.querySelector('[data-rcp-dots]');
        this.current = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-on')));
        this.cartForm = this.querySelector('product-form form');
        this.bar = this.querySelector('[data-rcp-bar]');

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        if (window.matchMedia('(hover: hover)').matches) {
          this.addEventListener('mouseover', (e) => {
            const el = e.target.closest('[data-rcp-size-dlg] th[data-rcp-row], [data-rcp-size-dlg] .rcp-ml, [data-rcp-size-dlg] .rcp-lg');
            const row = el ? parseInt(el.dataset.rcpRow, 10) : null;
            if (row !== this.meaRow) this.lightRow(row);
          });
        }
        const qty = this.querySelector('[data-rcp-qty]');
        if (qty) qty.addEventListener('input', () => this.update());
        this.mf = this.querySelector('[data-rcp-mf]');
        if (this.mf) {
          const fix = (e) => {
            const f = e.target.closest('[data-rcp-mf-field]');
            if (!f) return;
            if (f.value.trim()) this.memberMark(f, false);
            if (f.dataset.rcpMfField === 'LAST NAME') this.update();
          };
          this.mf.addEventListener('input', fix);
          this.mf.addEventListener('change', fix);
        }
        const form = this.querySelector('product-form');
        if (form) form.addEventListener('submit', this.onSubmit.bind(this), true);
        if (this.viewer) this.bindViewer();

        this.header = document.querySelector('.section-header');
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            let top = 0;
            if (this.header) {
              const r = this.header.getBoundingClientRect();
              if (r.top <= 1 && r.bottom > 0) top = Math.round(r.bottom);
            }
            this.headerBottom = top;
            this.style.setProperty('--rcp-top', top + 'px');
            this.barMode();
            this.barEnd();
            this.spy();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);

        this.setupBar();
        this.setupDeco();
        this.setupLb();
        this.renderDots();
        this.update();
        this.onScroll();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      readJson(selector, fallback) {
        const el = this.querySelector(selector);
        try {
          return el ? JSON.parse(el.textContent) : fallback;
        } catch (e) {
          return fallback;
        }
      }

      fmt(cents) {
        return this.money.format(cents / 100);
      }

      /* ---------- events ---------- */
      onClick(e) {
        const opt = e.target.closest('[data-rcp-option] button[data-value]');
        if (opt) {
          if (opt.classList.contains('is-soldout')) return;
          this.choose(parseInt(opt.closest('[data-rcp-option]').dataset.rcpOption, 10), opt.dataset.value);
          return;
        }
        const barColour = e.target.closest('[data-rcp-bar-colour]');
        if (barColour) {
          this.choose(this.colourIndex, barColour.dataset.rcpBarColour);
          return;
        }
        const q = e.target.closest('[data-rcp-q]');
        if (q) {
          const input = this.querySelector('[data-rcp-qty]');
          input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(q.dataset.rcpQ, 10));
          this.update();
          return;
        }
        const go = e.target.closest('[data-rcp-go]');
        if (go) {
          this.show(parseInt(go.dataset.rcpGo, 10));
          return;
        }
        const play = e.target.closest('[data-rcp-play]');
        if (play) {
          this.playVideo(play.closest('[data-rcp-video]'));
          return;
        }
        if (e.target.closest('[data-rcp-viewer]')) {
          const slide = this.slides[this.current];
          if (slide && !slide.hasAttribute('data-rcp-video')) this.openLb();
          return;
        }
        const lbStep = e.target.closest('[data-rcp-lb-step]');
        if (lbStep) {
          this.lbStep(parseInt(lbStep.dataset.rcpLbStep, 10));
          return;
        }
        if (e.target.matches('[data-rcp-lb-img]')) {
          this.lbZoom(e);
          return;
        }
        if (e.target.matches('[data-rcp-lb-stage]')) {
          e.target.closest('dialog').close(); /* a click beside the photo */
          return;
        }
        if (e.target.closest('[data-rcp-top]')) {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        const sw = e.target.closest('[data-rcp-addon]');
        if (sw) {
          this.addonOn = sw.getAttribute('aria-checked') !== 'true';
          sw.setAttribute('aria-checked', this.addonOn ? 'true' : 'false');
          const card = this.querySelector('[data-rcp-up]');
          if (card) card.classList.toggle('is-on', this.addonOn);
          this.update();
          return;
        }
        if (e.target.closest('[data-rcp-express-cover]')) {
          this.nudge(this.need, 'main');
          return;
        }
        if (e.target.closest('[data-rcp-kit-add]')) {
          this.addKit();
          return;
        }
        if (e.target.closest('[data-rcp-bar-go]')) {
          if (this.need >= 0) {
            this.nudge(this.need, 'bar');
            return;
          }
          if (this.cartForm) this.cartForm.requestSubmit();
          return;
        }
        const navLink = e.target.closest('[data-rcp-nav] a, [data-rcp-sheet-list] a');
        if (navLink) {
          e.preventDefault();
          const sheet = this.querySelector('[data-rcp-sheet]');
          if (sheet && sheet.open) sheet.close();
          this.goTo(navLink.dataset.target);
          return;
        }
        if (e.target.closest('[data-rcp-sheet-open]')) {
          const sheet = this.querySelector('[data-rcp-sheet]');
          if (sheet && sheet.showModal) sheet.showModal();
          return;
        }
        if (e.target.closest('[data-rcp-deco-open]')) {
          const d = this.querySelector('[data-rcp-deco]');
          if (d && d.showModal) d.showModal();
          return;
        }
        if (e.target.closest('[data-rcp-size-open]')) {
          const d = this.querySelector('[data-rcp-size-dlg]');
          if (d && d.showModal) d.showModal();
          return;
        }
        const unit = e.target.closest('[data-rcp-unit]');
        if (unit) {
          this.unit = unit.dataset.rcpUnit;
          this.updateSize();
          return;
        }
        const rowEl = e.target.closest('[data-rcp-size-dlg] [data-rcp-row]');
        if (rowEl && !rowEl.matches('td')) {
          const row = parseInt(rowEl.dataset.rcpRow, 10);
          this.lightRow(this.meaRow === row ? null : row);
          return;
        }
        const close = e.target.closest('[data-rcp-close]');
        if (close) {
          close.closest('dialog').close();
          return;
        }
        if (e.target.matches('dialog.rcp-sheet, dialog.rcp-deco, dialog.rcp-drawer')) {
          const r = e.target.getBoundingClientRect(); /* a click on the backdrop, outside the panel */
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) e.target.close();
          return;
        }
        const want = e.target.closest('[data-rcp-want] button');
        if (want) {
          want.setAttribute('aria-pressed', want.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
          const tags = this.querySelector('[data-rcp-tags]');
          if (tags) {
            const on = Array.from(this.querySelectorAll('[data-rcp-want] button[aria-pressed="true"]')).map((b) => b.dataset.tag);
            tags.value = [tags.dataset.base].concat(on).join(',');
          }
        }
      }

      onChange(e) {
        const barSel = e.target.closest('[data-rcp-bar-opt]');
        if (barSel) {
          const i = parseInt(barSel.dataset.rcpBarOpt, 10);
          if (barSel.value) this.choose(i, barSel.value);
          return;
        }
        if (e.target.closest('[data-rcp-kit]')) {
          const msg = this.querySelector('[data-rcp-kit-msg]');
          if (msg) msg.hidden = true;
          this.updateKit();
        }
      }

      onSubmit(e) {
        if (this.need >= 0) {
          e.preventDefault();
          e.stopPropagation();
          this.nudge(this.need, 'main');
          return;
        }
        const member = this.memberDetails();
        if (member && !member.ok) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (!this.addonOn && !member) return; /* product-form.js adds the product */
        e.preventDefault();
        e.stopPropagation();
        const v = this.variant();
        const sw = this.querySelector('[data-rcp-addon]');
        const qty = Math.max(1, parseInt(this.querySelector('[data-rcp-qty]').value, 10) || 1);
        const main = { id: v.id, quantity: qty };
        if (member) main.properties = member.props;
        const items = [main];
        if (this.addonOn && sw) items.push({ id: Number(sw.dataset.rcpAddon), quantity: 1 });
        if (member && member.emb) {
          /* one name per garment, tied to the garment on the order (EasyFlow did the same) */
          items.push({
            id: member.emb.id,
            quantity: qty,
            properties: { 'LAST NAME': member.props['LAST NAME'], For: this.mf.dataset.for + ' / ' + v.o.join(' / ') },
          });
        }
        this.addItems(items, this.querySelector('[data-rcp-atc]')).catch((err) => this.hint(err.message));
      }

      /* ---------- department member details (EasyFlow stopgap, 2026-10-09) ---------- */
      memberEmbroidery() {
        if (!this.mf || !this.mf.dataset.embroidery) return null;
        const name = this.mf.querySelector('[data-rcp-mf-field="LAST NAME"]');
        if (!name || !name.value.trim()) return null;
        return { id: Number(this.mf.dataset.embroidery), price: parseInt(this.mf.dataset.embroideryPrice, 10) || 0 };
      }

      memberMark(f, bad) {
        const fld = f.closest('.rcp-mf__fld');
        if (fld) fld.classList.toggle('is-bad', bad);
        f.setAttribute('aria-invalid', bad ? 'true' : 'false');
        if (!bad) {
          const msg = this.mf.querySelector('[data-rcp-mf-msg]');
          if (msg && !this.mf.querySelector('.rcp-mf__fld.is-bad')) msg.hidden = true;
        }
      }

      /* null = no details asked here; { ok: false } = one is missing (marked and focused); else the properties */
      memberDetails() {
        if (!this.mf) return null;
        const fields = Array.from(this.mf.querySelectorAll('[data-rcp-mf-field]'));
        let first = null;
        const props = {};
        fields.forEach((f) => {
          const val = f.value.trim();
          const bad = f.required && !val;
          this.memberMark(f, bad);
          if (bad && !first) first = f;
          if (val) props[f.dataset.rcpMfField] = val;
        });
        const msg = this.mf.querySelector('[data-rcp-mf-msg]');
        if (first) {
          if (msg) {
            msg.textContent = 'Add ' + first.dataset.rcpMfLabel + ' to put this in your cart.';
            msg.hidden = false;
          }
          first.scrollIntoView({ block: 'center', behavior: 'smooth' });
          first.focus({ preventScroll: true });
          return { ok: false };
        }
        return { ok: true, props: props, emb: this.memberEmbroidery() };
      }

      choose(i, value) {
        const colourChanged = i === this.colourIndex && this.sel[i] !== value;
        this.sel[i] = value;
        this.touched = true;
        this.hint('');
        this.update();
        if (colourChanged) this.filterGallery();
      }

      /* ---------- options -> variant ---------- */
      matches(v, skip) {
        return this.sel.every((val, i) => i === skip || val === null || v.o[i] === val);
      }

      variant() {
        if (this.sel.some((val) => val === null)) return null;
        return this.variants.find((v) => this.matches(v, -1)) || null;
      }

      optionName(i) {
        const lbl = this.querySelector('[data-rcp-option="' + i + '"] .rcp-lbl span');
        return lbl ? lbl.textContent.trim().toLowerCase() : 'size';
      }

      available(i, value) {
        return this.variants.some((x) => x.a && x.o[i] === value && this.matches(x, i));
      }

      update() {
        const v = this.variant();
        const pool = this.variants.filter((x) => this.matches(x, -1));
        const stand = v || pool.find((x) => x.a) || pool[0] || this.variants[0];
        this.stand = stand;

        this.querySelectorAll('[data-rcp-id]').forEach((input) => {
          if (input.value !== String(stand.id)) {
            input.value = stand.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        this.querySelectorAll('[data-rcp-option]').forEach((fs) => {
          const i = parseInt(fs.dataset.rcpOption, 10);
          const chosen = fs.querySelector('[data-rcp-chosen]');
          if (chosen) chosen.textContent = this.sel[i] || '';
          fs.querySelectorAll('button[data-value]').forEach((b) => {
            b.setAttribute('aria-pressed', b.dataset.value === this.sel[i] ? 'true' : 'false');
            if (i === this.colourIndex) return;
            const ok = this.available(i, b.dataset.value);
            b.classList.toggle('is-soldout', !ok);
            b.setAttribute('aria-disabled', ok ? 'false' : 'true');
          });
        });

        const unit = v ? v.p : Math.min.apply(null, (pool.length ? pool : this.variants).map((x) => x.p));
        this.unit = unit;
        const qty = Math.max(1, parseInt((this.querySelector('[data-rcp-qty]') || {}).value, 10) || 1);
        const sw = this.querySelector('[data-rcp-addon]');
        const addonPrice = this.addonOn && sw ? parseInt(sw.dataset.price, 10) : 0;
        const emb = this.memberEmbroidery();
        const embPrice = emb ? emb.price * qty : 0;
        this.setText('[data-rcp-price]', this.fmt(unit));
        const compare = this.querySelector('[data-rcp-compare]');
        if (compare) {
          const c = v ? v.c : 0;
          compare.hidden = !(c > unit);
          if (c > unit) compare.textContent = this.fmt(c);
        }

        const missing = this.sel.findIndex((val) => val === null);
        this.need = missing >= 0 ? missing : -1;
        let label = this.dataset.addLabel;
        if (this.need >= 0) label = 'Choose a ' + this.optionName(this.need);
        else if (!v) label = this.dataset.unavailableLabel;
        else if (!v.a) label = this.dataset.soldLabel;
        else if (this.addonOn) label = 'Add ' + (qty + 1) + ' items';
        else if (emb) label = 'Add with name';
        const blocked = this.need < 0 && (!v || !v.a);
        const total = this.need >= 0 ? unit : unit * qty + addonPrice + embPrice;
        const atc = this.querySelector('[data-rcp-atc]');
        if (atc) atc.toggleAttribute('disabled', blocked);
        this.setText('[data-rcp-atc-label]', label);
        this.setText('[data-rcp-atc-price]', this.fmt(total));

        const cover = this.querySelector('[data-rcp-express-cover]');
        if (cover) cover.hidden = this.need < 0;

        this.updateBar(v, label, total, blocked);
        this.updateKit();
        this.updateSize();

        if (v && this.touched) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      setText(selector, text) {
        this.querySelectorAll(selector).forEach((el) => {
          el.textContent = text;
        });
      }

      nudge(i, where) {
        const name = this.optionName(i);
        this.hint('Choose a ' + name + ' first.');
        if (where === 'bar') {
          const sel = this.querySelector('[data-rcp-bar-opt="' + i + '"]');
          if (sel && sel.offsetParent) {
            sel.classList.add('is-flash');
            sel.focus();
            setTimeout(() => sel.classList.remove('is-flash'), 1400);
            return;
          }
        }
        const fs = this.querySelector('[data-rcp-option="' + i + '"]');
        if (!fs) return;
        fs.classList.remove('is-nudge');
        void fs.offsetWidth;
        fs.classList.add('is-nudge');
        setTimeout(() => fs.classList.remove('is-nudge'), 900);
        const first = fs.querySelector('button[data-value]:not(.is-soldout)');
        if (first) first.focus({ preventScroll: true });
        const r = fs.getBoundingClientRect();
        if (r.top < 80 || r.bottom > window.innerHeight - 80) fs.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      hint(text) {
        const el = this.querySelector('[data-rcp-hint]');
        if (!el) return;
        el.textContent = text;
        el.hidden = !text;
      }

      /* one request for several lines; the cart drawer gets its sections and opens */
      addItems(items, button) {
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'];
        const url = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
        if (button) {
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');
          const spin = button.querySelector('.loading__spinner');
          if (spin) spin.classList.remove('hidden');
        }
        const done = () => {
          if (!button) return;
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          const spin = button.querySelector('.loading__spinner');
          if (spin) spin.classList.add('hidden');
        };
        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: items, sections: sections, sections_url: window.location.pathname }),
        })
          .then((r) =>
            r.json().then((j) => {
              if (!r.ok || j.status) throw new Error(j.description || j.message || 'Could not add these items.');
              if (drawer && typeof drawer.renderContents === 'function') {
                drawer.classList.remove('is-empty'); /* as product-form.js: open() traps focus in the filled drawer */
                drawer.renderContents(j);
              } else {
                window.location = (window.routes && window.routes.cart_url) || '/cart';
              }
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rc-product', cartData: j });
              }
              return j;
            })
          )
          .finally(done);
      }

      /* ---------- size guide: the chosen size in both charts and on the measurement lines ---------- */
      /* RC: page 72 (2026-09-27): the numbers read in inches or centimetres (unit switch in the drawer; GAME's
         inch figures as printed, cm to the half); a letter, a column head or a line lights its line and column. */
      measure(inches) {
        const v = parseFloat(inches);
        if (isNaN(v)) return String(inches || '').trim();
        if (this.unit === 'cm') return String(Math.round(v * 2.54 * 2) / 2);
        return String(inches).trim();
      }

      updateSize() {
        if (!this.chart || !this.chart.sizes) return;
        const chosen = this.sizeIndex >= 0 ? this.sel[this.sizeIndex] : null;
        const known = chosen && this.chart.sizes.indexOf(chosen) >= 0;
        const shown = known ? chosen : this.chart.default;
        const i = this.chart.sizes.indexOf(shown);
        const suffix = this.unit === 'cm' ? ' cm' : '″';
        this.querySelectorAll('[data-rcp-size]').forEach((el) => el.classList.toggle('is-pick', known && el.dataset.rcpSize === chosen));
        this.querySelectorAll('[data-rcp-m]').forEach((el) => {
          const row = this.chart.rows[parseInt(el.dataset.rcpM, 10)] || [];
          const val = row[i] ? this.measure(row[i]) : '';
          el.textContent = val ? val + suffix : '';
        });
        this.querySelectorAll('[data-rcp-in]').forEach((el) => {
          el.textContent = this.measure(el.dataset.rcpIn);
        });
        this.querySelectorAll('[data-rcp-unit-label]').forEach((el) => {
          el.textContent = this.unit === 'cm' ? 'Centimetres' : 'Inches';
        });
        this.querySelectorAll('[data-rcp-unit]').forEach((el) => {
          el.setAttribute('aria-pressed', el.dataset.rcpUnit === (this.unit || 'in') ? 'true' : 'false');
        });
        this.setText('[data-rcp-m-size]', shown || '');
        this.querySelectorAll('[data-rcp-m-hint]').forEach((el) => {
          el.hidden = known;
        });
      }

      lightRow(row) {
        this.meaRow = row;
        const on = row !== null && row !== undefined;
        this.querySelectorAll('[data-rcp-fig]').forEach((f) => f.classList.toggle('is-dim', on));
        this.querySelectorAll('[data-rcp-row]').forEach((el) => {
          el.classList.toggle('is-on', on && parseInt(el.dataset.rcpRow, 10) === row);
        });
      }

      /* ---------- build your kit ---------- */
      updateKit() {
        const kit = this.querySelector('[data-rcp-kit]');
        if (!kit) return;
        const pct = parseFloat(kit.dataset.pct || '10') / 100;
        const anchor = kit.querySelector('[data-rcp-kit-anchor]');
        const colour = this.sel[this.colourIndex];
        const size = this.need >= 0 ? 'choose a ' + this.optionName(this.need) : this.sel.filter((x, i) => i !== this.colourIndex && x).join(' · ');
        const meta = kit.querySelector('[data-rcp-kit-anchor-meta]');
        if (meta) meta.textContent = [colour, size].filter(Boolean).join(' · ');
        this.setText('[data-rcp-kit-anchor-price]', this.fmt(this.unit));
        const img = kit.querySelector('[data-rcp-kit-anchor-img]');
        const card = this.querySelector('.rcp-opt--colour button[aria-pressed="true"] img');
        if (img && card && card.currentSrc) img.src = card.currentSrc.replace(/width=\d+/, 'width=360');

        let sum = anchor && anchor.checked ? this.unit : 0;
        let save = 0;
        let addons = 0;
        kit.querySelectorAll('[data-rcp-kit-item]').forEach((item) => {
          const pick = item.querySelector('[data-rcp-kit-variant]');
          const opt = pick.tagName === 'SELECT' ? pick.selectedOptions[0] : pick;
          const price = parseInt((opt && opt.dataset.price) || item.querySelector('[data-rcp-kit-price]').dataset.rcpKitPrice, 10);
          item.querySelector('[data-rcp-kit-price]').textContent = this.fmt(price);
          if (!item.querySelector('[data-rcp-kit-check]').checked) return;
          sum += price;
          addons += 1;
          if (anchor && anchor.checked && addons <= 3) save += Math.floor(price * pct + 1e-9);
        });
        let line;
        if (!anchor || !anchor.checked) line = 'Keep ' + (kit.dataset.noun || 'this item') + ' in the kit to save';
        else if (!addons) line = 'Pick an add-on to save ' + Math.round(pct * 100) + ' %';
        else line = 'Kit price · you save ' + this.fmt(save);
        this.setText('[data-rcp-kit-save]', line);
        this.setText('[data-rcp-kit-was]', save > 0 ? this.fmt(sum) : '');
        this.setText('[data-rcp-kit-now]', this.fmt(sum - save));
        const add = kit.querySelector('[data-rcp-kit-add]');
        if (add) add.disabled = !(anchor && anchor.checked) && !addons;
      }

      addKit() {
        const kit = this.querySelector('[data-rcp-kit]');
        const msg = kit.querySelector('[data-rcp-kit-msg]');
        const say = (t) => {
          msg.textContent = t;
          msg.hidden = !t;
        };
        say('');
        const items = [];
        const anchor = kit.querySelector('[data-rcp-kit-anchor]');
        if (anchor && anchor.checked) {
          if (this.need >= 0) {
            say('Choose a ' + this.optionName(this.need) + ' above first.');
            this.nudge(this.need, 'main');
            return;
          }
          const v = this.variant();
          if (!v || !v.a) {
            const n = kit.dataset.noun || 'this item';
            say(n.charAt(0).toUpperCase() + n.slice(1) + (n.startsWith('these') ? ' are' : ' is') + ' sold out in that choice.');
            return;
          }
          items.push({ id: v.id, quantity: 1 });
        }
        for (const item of kit.querySelectorAll('[data-rcp-kit-item]')) {
          if (!item.querySelector('[data-rcp-kit-check]').checked) continue;
          const pick = item.querySelector('[data-rcp-kit-variant]');
          if (!pick.value) {
            say('Choose the ' + pick.options[0].textContent.replace(/^Choose /, '') + ' for ' + item.querySelector('b').textContent + '.');
            pick.classList.add('is-flash');
            pick.focus();
            setTimeout(() => pick.classList.remove('is-flash'), 1400);
            return;
          }
          items.push({ id: Number(pick.value), quantity: 1 });
        }
        if (!items.length) return;
        this.addItems(items, kit.querySelector('[data-rcp-kit-add]')).catch((err) => say(err.message));
      }

      /* ---------- bottom bar ---------- */
      setupBar() {
        if (!this.bar) return;
        const secs = Array.from(this.querySelectorAll('[data-rcp-sec]'));
        this.secs = secs;
        const links = secs.map((s) => '<a href="#' + s.id + '" data-target="' + s.id + '">' + s.dataset.label + '</a>').join('');
        const nav = this.querySelector('[data-rcp-nav]');
        const list = this.querySelector('[data-rcp-sheet-list]');
        if (nav) nav.innerHTML = links;
        if (list) list.innerHTML = links;
        if (!secs.length) {
          const btn = this.querySelector('[data-rcp-sheet-open]');
          if (btn) btn.hidden = true;
        }
        this.bar.classList.toggle('is-empty', !secs.length);
      }

      updateBar(v, label, total, blocked) {
        if (!this.bar) return;
        const colour = this.sel[this.colourIndex];
        const rest = this.sel.filter((x, i) => i !== this.colourIndex && x);
        const meta = [colour].concat(this.need >= 0 ? ['choose a ' + this.optionName(this.need)] : rest);
        if (this.addonOn) meta.push('+ add-on');
        this.setText('[data-rcp-bar-meta]', meta.filter(Boolean).join(' · '));
        this.bar.querySelectorAll('[data-rcp-bar-colour]').forEach((b) => b.setAttribute('aria-checked', b.dataset.rcpBarColour === colour ? 'true' : 'false'));
        this.bar.querySelectorAll('[data-rcp-bar-opt]').forEach((s) => {
          const i = parseInt(s.dataset.rcpBarOpt, 10);
          s.value = this.sel[i] || '';
          Array.from(s.options).forEach((o) => {
            if (o.value && i !== this.colourIndex) o.disabled = !this.available(i, o.value);
          });
        });
        const th = this.bar.querySelector('[data-rcp-bar-th]');
        const card = this.querySelector('.rcp-opt--colour button[aria-pressed="true"] img');
        if (th && card) th.src = card.getAttribute('src');
        this.setText('[data-rcp-bar-label]', this.need >= 0 ? 'Choose ' + this.optionName(this.need) : label);
        this.setText('[data-rcp-bar-price]', this.need >= 0 ? '' : ' · ' + this.fmt(total));
        const go = this.bar.querySelector('[data-rcp-bar-go]');
        if (go) go.toggleAttribute('disabled', blocked);
      }

      /* the bar stays to the bottom of the page: the page keeps its height free at the end, and past the last
         section the bar offers Back to top */
      barEnd() {
        if (!this.bar) return;
        const h = this.bar.offsetHeight;
        if (h !== this.barH) {
          this.barH = h;
          document.documentElement.style.setProperty('--rcp-bar-h', h + 'px');
        }
        const top = this.bar.querySelector('[data-rcp-top]');
        if (!top) return;
        const secs = this.querySelectorAll('.rcp-sec');
        const last = secs[secs.length - 1];
        top.hidden = !last || last.getBoundingClientRect().bottom > window.innerHeight - h;
        this.bar.classList.toggle('is-end', !top.hidden);
      }

      /* the buy side shows once most of the main Add to cart button has gone up behind the header */
      barMode() {
        if (!this.bar) return;
        const atc = this.querySelector('[data-rcp-atc]');
        if (!atc) return;
        const r = atc.getBoundingClientRect();
        this.bar.classList.toggle('is-buy', r.top + r.height * 0.4 < (this.headerBottom || 0));
      }

      spy() {
        if (!this.secs || !this.secs.length || this.lock) return;
        const top = this.headerBottom || 0;
        const barH = this.bar ? this.bar.offsetHeight : 0;
        const line = top + (window.innerHeight - top - barH) * 0.33;
        let cur = null;
        this.secs.forEach((s) => {
          if (s.getBoundingClientRect().top <= line) cur = s.id;
        });
        this.setActive(cur);
      }

      setActive(id) {
        if (this.active === id) return;
        this.active = id;
        this.querySelectorAll('[data-rcp-nav] a').forEach((a) => a.classList.toggle('is-on', a.dataset.target === id));
      }

      goTo(id) {
        const el = document.getElementById(id);
        if (!el) return;
        this.lock = true;
        this.setActive(id);
        const top = el.getBoundingClientRect().top + window.scrollY - (this.headerBottom || 0) - 8;
        window.scrollTo({ top: top, behavior: 'smooth' });
        clearTimeout(this.lockTimer);
        this.lockTimer = setTimeout(() => {
          this.lock = false;
        }, 1200);
      }

      /* ---------- decoration pop-up ---------- */
      setupDeco() {
        const d = this.querySelector('[data-rcp-deco]');
        if (!d) return;
        const form = d.querySelector('[data-rcp-notify]');
        if (form) {
          form.addEventListener('submit', () => {
            try {
              sessionStorage.setItem('rcp-notify', '1');
            } catch (e) {}
          });
        }
        let flagged = false;
        try {
          flagged = sessionStorage.getItem('rcp-notify') === '1';
          sessionStorage.removeItem('rcp-notify');
        } catch (e) {}
        const posted = /customer_posted=true/.test(window.location.search) || d.querySelector('[data-rcp-notify-err]');
        if (flagged && posted && d.showModal) d.showModal();
      }

      /* ---------- gallery ---------- */
      visible() {
        return this.slides.filter((s) => !s.hidden);
      }

      filterGallery() {
        const key = (this.sel[this.colourIndex] || '').toLowerCase();
        const keep = (el) => !el.dataset.colour || el.dataset.colour === key;
        this.slides.forEach((s) => {
          s.hidden = !keep(s);
        });
        this.thumbs.forEach((t) => {
          t.hidden = !keep(t);
        });
        this.querySelectorAll('.rcp-up__base[data-colour], [data-rcp-cv][data-colour]').forEach((img) => {
          img.hidden = !keep(img);
        });
        const first = this.visible()[0];
        this.renderDots();
        if (first) this.show(parseInt(first.dataset.rcpSlide, 10));
      }

      show(i) {
        const target = this.slides[i];
        if (!target || target.hidden) return;
        this.slides.forEach((s) => {
          if (s !== target && s.classList.contains('is-on')) this.stopVideo(s);
          s.classList.toggle('is-on', s === target);
        });
        this.thumbs.forEach((t) => t.setAttribute('aria-current', parseInt(t.dataset.rcpGo, 10) === i ? 'true' : 'false'));
        this.current = i;
        const pos = this.visible().indexOf(target);
        if (this.dots) Array.from(this.dots.children).forEach((d, k) => d.classList.toggle('is-on', k === pos));
        const thumb = this.thumbs.find((t) => parseInt(t.dataset.rcpGo, 10) === i);
        const rail = thumb && thumb.parentElement;
        if (rail && rail.scrollHeight > rail.clientHeight) {
          const top = thumb.offsetTop - rail.offsetTop;
          if (top < rail.scrollTop || top + thumb.offsetHeight > rail.scrollTop + rail.clientHeight) rail.scrollTo({ top: top, behavior: 'smooth' });
        }
      }

      step(dir) {
        const list = this.visible();
        if (list.length < 2) return;
        const pos = list.indexOf(this.slides[this.current]);
        const next = list[(pos + dir + list.length) % list.length];
        this.show(parseInt(next.dataset.rcpSlide, 10));
      }

      renderDots() {
        if (!this.dots) return;
        const n = this.visible().length;
        this.dots.innerHTML = n > 1 ? new Array(n + 1).join('<i></i>') : '';
        const pos = this.visible().indexOf(this.slides[this.current]);
        if (this.dots.children[pos]) this.dots.children[pos].classList.add('is-on');
      }

      bindViewer() {
        this.viewer.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.step(1);
          else if (e.key === 'ArrowLeft') this.step(-1);
          else if (e.key === 'Enter') this.openLb();
          else return;
          e.preventDefault();
        });
        /* hover zoom (mouse only): the photo at 2000 px behind a layer that follows the pointer */
        const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
        const zoom = document.createElement('div');
        zoom.className = 'rcp-zoom';
        zoom.hidden = true;
        zoom.setAttribute('aria-hidden', 'true');
        this.viewer.appendChild(zoom);
        this.viewer.addEventListener('mousemove', (e) => {
          const slide = this.slides[this.current];
          if (!fine.matches || !slide || !slide.dataset.zoom || slide.hasAttribute('data-rcp-video')) {
            zoom.hidden = true;
            return;
          }
          if (zoom.dataset.src !== slide.dataset.zoom) {
            zoom.dataset.src = slide.dataset.zoom;
            zoom.style.backgroundImage = 'url("' + slide.dataset.zoom + '")';
            zoom.classList.toggle('is-life', slide.classList.contains('rcp-slide--life'));
          }
          const r = this.viewer.getBoundingClientRect();
          const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
          const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
          zoom.style.backgroundPosition = x + '% ' + y + '%';
          zoom.hidden = false;
        });
        this.viewer.addEventListener('mouseleave', () => {
          zoom.hidden = true;
        });
        let x0 = null;
        let y0 = null;
        this.viewer.addEventListener(
          'touchstart',
          (e) => {
            x0 = e.touches[0].clientX;
            y0 = e.touches[0].clientY;
          },
          { passive: true }
        );
        this.viewer.addEventListener(
          'touchend',
          (e) => {
            if (x0 === null) return;
            const dx = e.changedTouches[0].clientX - x0;
            const dy = e.changedTouches[0].clientY - y0;
            x0 = null;
            if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.step(dx < 0 ? 1 : -1);
          },
          { passive: true }
        );
      }

      /* ---------- photos large: the chosen colour's photos in a full-screen viewer ---------- */
      setupLb() {
        this.lb = this.querySelector('[data-rcp-lb]');
        if (!this.lb) return;
        this.lbImg = this.lb.querySelector('[data-rcp-lb-img]');
        this.lbStage = this.lb.querySelector('[data-rcp-lb-stage]');
        this.lb.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.lbStep(1);
          else if (e.key === 'ArrowLeft') this.lbStep(-1);
          else return;
          e.preventDefault();
        });
        let x0 = null;
        this.lbStage.addEventListener('touchstart', (e) => {
          x0 = this.lbStage.classList.contains('is-zoomed') ? null : e.touches[0].clientX;
        }, { passive: true });
        this.lbStage.addEventListener('touchend', (e) => {
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          x0 = null;
          if (Math.abs(dx) > 40) this.lbStep(dx < 0 ? 1 : -1);
        }, { passive: true });
      }

      openLb() {
        if (!this.lb || !this.lb.showModal) return;
        const photos = this.visible().filter((s) => s.dataset.zoom && !s.hasAttribute('data-rcp-video'));
        if (!photos.length) return;
        this.lbItems = photos.map((s) => ({ src: s.dataset.zoom, alt: (s.querySelector('img') || {}).alt || '' }));
        this.lbIndex = Math.max(0, photos.indexOf(this.slides[this.current]));
        this.lbShow();
        this.lb.showModal();
      }

      lbShow() {
        const it = this.lbItems[this.lbIndex];
        this.lbStage.classList.remove('is-zoomed');
        this.lbImg.src = it.src;
        this.lbImg.alt = it.alt;
        this.setText('[data-rcp-lb-count]', this.lbIndex + 1 + ' / ' + this.lbItems.length);
        this.lb.classList.toggle('is-single', this.lbItems.length < 2);
      }

      lbStep(dir) {
        if (!this.lbItems || this.lbItems.length < 2) return;
        this.lbIndex = (this.lbIndex + dir + this.lbItems.length) % this.lbItems.length;
        this.lbShow();
      }

      /* click the large photo: full size, scrolled to where it was clicked; click again to fit */
      lbZoom(e) {
        const stage = this.lbStage;
        const img = this.lbImg;
        if (stage.classList.contains('is-zoomed')) {
          stage.classList.remove('is-zoomed');
          return;
        }
        const r = img.getBoundingClientRect();
        const fx = (e.clientX - r.left) / r.width;
        const fy = (e.clientY - r.top) / r.height;
        stage.classList.add('is-zoomed');
        stage.scrollLeft = fx * img.scrollWidth - stage.clientWidth / 2;
        stage.scrollTop = fy * img.scrollHeight - stage.clientHeight / 2;
      }

      playVideo(slide) {
        if (!slide) return;
        const tpl = slide.querySelector('template');
        if (!tpl) return;
        slide.querySelectorAll(':scope > img, :scope > .rcp-play').forEach((el) => el.remove());
        slide.appendChild(tpl.content.cloneNode(true));
        const video = slide.querySelector('video');
        if (video) video.play().catch(() => {});
      }

      stopVideo(slide) {
        if (!slide.hasAttribute('data-rcp-video')) return;
        const player = slide.querySelector('iframe, video');
        if (player && player.tagName === 'VIDEO') player.pause();
        else if (player) player.src = player.src;
      }
    }
  );
}
